import ITEM_RULES from "@/constants/item-rule";
import { find, isNil, isEmpty, isNaN } from "lodash";
import ItemRuleService from "@/services/items/items-rule";

export default {
    data() {
        return {
            showSendToLab: false
        };
    },
    methods: {
        /**
         * checkRuleItem
         * @param {*} patient Need property:
         * id,
         * species(id),
         * weight_in_grams (if empty will query API),
         * age_in_days(if empty will query API),
         * sex,
         * patient_breeds
         * @param {*} item Need property:
         * item_rules
         * @returns
         */
        async checkRuleItem(patient, item) {
            const rules = item.item_rules;
            let self = this;

            if (isNil(rules) || rules.length === 0) {
                if (isNil(item)) {
                    console.error("Item rules is null / undefined.");
                }
                return {
                    isPassed: true,
                    message: "Passed as item has no rules"
                };
            }

            let isRulePassed = true;
            let message = "";

            const isRulesHasAgeOrWeight = rules.some(rule => {
                return [
                    ITEM_RULES.TYPE_AGE.key,
                    ITEM_RULES.TYPE_WEIGHT.key
                ].includes(Number(rule.rule_type));
            });
            if (
                isRulesHasAgeOrWeight &&
                (!Object.prototype.hasOwnProperty.call(
                    patient,
                    "age_in_days"
                ) ||
                    !Object.prototype.hasOwnProperty.call(
                        patient,
                        "weight_in_grams"
                    ))
            ) {
                let resPatientAgeWeight = await ItemRuleService.getPatientAgeWeight(
                    patient.id
                );
                if (resPatientAgeWeight) {
                    let rawWeight = resPatientAgeWeight.weight_in_grams;
                    let weightInGrams =
                        typeof rawWeight === "string"
                            ? parseFloat(rawWeight.replace(/,/g, ""))
                            : parseFloat(rawWeight);

                    resPatientAgeWeight.weight_in_grams = isNaN(weightInGrams)
                        ? 0
                        : weightInGrams;
                    Object.assign(patient, resPatientAgeWeight);
                }
            }
            const unitToDays = { year: 365, month: 30, week: 7 };
            const unitToGrams = { g: 1, kg: 1000, lbs: 453.592, oz: 28.3495 };
            let rulesNotMatched = [];
            rules
                .sort((a, b) => a.rule_type - b.rule_type)
                .forEach(rule => {
                    const type = Number(rule.rule_type);
                    if (type === ITEM_RULES.TYPE_SPECIES.key) {
                        const match = find(rule.rule_value.species, {
                            species_id: patient.species
                        });

                        if (!match) {
                            rulesNotMatched.push("species");
                            return;
                        }

                        // Check Breeds
                        if (match.breed_ids[0] === -1) {
                            // All breeds allowed
                        } else {
                            const patientBreeds = patient.patient_breeds.map(
                                b => b.id
                            );
                            const breedSet = new Set(match.breed_ids);

                            const allIncluded = patientBreeds.some(id =>
                                breedSet.has(id)
                            );

                            if (!allIncluded) {
                                rulesNotMatched.push("breed");
                                return;
                            }
                        }

                        // Check Sexes
                        if (match.sex_ids[0] === -1) {
                            // All sex allowed
                        } else {
                            const patientSex = patient.sex;
                            const sexSet = new Set(match.sex_ids);
                            const allIncludedSex = sexSet.has(patientSex);

                            if (!allIncludedSex) {
                                rulesNotMatched.push("sex");
                                return;
                            }
                        }
                    } else if (type === ITEM_RULES.TYPE_AGE.key) {
                        let birthdate = patient.birthdate; // In carbon

                        if (isNil(birthdate) || isEmpty(birthdate)) {
                            birthdate = patient.est_birthdate; // In carbon
                        }
                        if (isNil(birthdate) || isEmpty(birthdate)) {
                            return;
                        }

                        const { min, max, unit_label } = rule.rule_value;

                        // USING BIRTHDATE
                        const birthdateDatetime = this._fromDateCarbon(
                            birthdate
                        );
                        const now = this._now();

                        let minBirthdate = null;
                        let maxBirthdate = null;

                        // Calculate valid birthdate range from today
                        if (!isNil(max) && max !== "") {
                            minBirthdate = now
                                .minus({
                                    [unit_label.toLowerCase()]: parseFloat(max)
                                })
                                .startOf("day");
                        }

                        if (!isNil(min) && min !== "") {
                            maxBirthdate = now
                                .minus({
                                    [unit_label.toLowerCase()]: parseFloat(min)
                                })
                                .endOf("day");
                        }

                        // Skip check if both min and max are null
                        if (minBirthdate || maxBirthdate) {
                            const isTooOld =
                                minBirthdate &&
                                birthdateDatetime < minBirthdate;
                            const isTooYoung =
                                maxBirthdate &&
                                birthdateDatetime > maxBirthdate;

                            if (isTooOld || isTooYoung) {
                                rulesNotMatched.push("age");
                                return;
                            }
                        }
                    } else if (type === ITEM_RULES.TYPE_WEIGHT.key) {
                        if (
                            patient.weight_in_grams === null ||
                            patient.weight_in_grams === "" ||
                            patient.weight_in_grams === 0 ||
                            patient.weight_in_grams === undefined
                        ) {
                            return;
                        }

                        const { min, max, unit_label } = rule.rule_value;
                        const unit = unit_label.toLowerCase();
                        const weight = patient.weight_in_grams;

                        let minGrams = null;
                        let maxGrams = null;

                        if (!isNil(min) && min !== "") {
                            minGrams = (
                                parseFloat(min) * unitToGrams[unit]
                            ).toFixed(2);
                        }

                        if (!isNil(max) && max !== "") {
                            maxGrams = (
                                parseFloat(max) * unitToGrams[unit]
                            ).toFixed(2);
                        }

                        // Skip check if both are null
                        if (minGrams !== null || maxGrams !== null) {
                            const isBelowMin =
                                minGrams !== null && weight < minGrams;
                            const isAboveMax =
                                maxGrams !== null && weight > maxGrams;

                            if (isBelowMin || isAboveMax) {
                                rulesNotMatched.push("weight");
                                return;
                            }
                        }
                    }
                });

            isRulePassed = rulesNotMatched.length === 0;
            if (!isRulePassed) {
                message = `Patient ${this._arrayToSentence(
                    rulesNotMatched
                )} does not match the item ${
                    rulesNotMatched.length > 1 ? "rules" : "rule"
                }`;
            }
            return { isPassed: isRulePassed, message: message };
        },
        /**
         * onVSelectOpen
         * @param {*} ref Vue Select ref
         * @returns
         * - Remove the highlight CSS for the first option in Vue Select to avoid user confusion when the option is actually disabled
         * - This flow only can be applied when the vue-select is appendToBody false or not set any
         */
        async onVSelectOpen(ref) {
            // Remove the highlight CSS for the first option in Vue Select to avoid user confusion when the option is actually disabled
            // This flow only can be applied when the vue-select is appendToBody false or not set any
            this.$nextTick(() => {
                const dropdown = this.$refs[ref].$el.querySelector(
                    ".vs__dropdown-menu"
                );
                const highlighted = dropdown?.querySelector(
                    ".vs__dropdown-option--highlight"
                );
                highlighted?.classList.remove("vs__dropdown-option--highlight");
            });
        },
        /**
         * Evaluates item rules for an array of items against a patient
         * @param {Object} patient - The patient to check rules against
         * @param {Array} items - Array of items to evaluate rules for
         * @param {Boolean} applyItemRule - Whether to apply item rules
         * @returns {Array} - The items array with updated disabled, selected, and tooltip properties
         */
        async checkRuleItemsBundle(patient, items, applyItemRule = true) {
            if (!patient || !Array.isArray(items)) {
                return items;
            }

            for (const item of items) {
                // Skip if no rules to check
                if (!applyItemRule || !item.item_rules?.length) {
                    this.$set(item, "disabled", false);
                    this.$set(item, "tooltip", "");
                    continue;
                }

                // Check rules and update item properties
                const result = await this.checkRuleItem(patient, item);

                // Use $set to ensure reactivity
                this.$set(item, "disabled", !result.isPassed);
                this.$set(item, "selected", result.isPassed);
                this.$set(item, "tooltip", result.message);
            }

            return items;
        }
    }
};
